<template>
  <div class="gps-main">
    <div v-if="showBackLink" class="header">
      <a @click="backLinkClicked()">{{ this.backLinkText }}</a>
    </div>

    <Spinner class="is-size-3" v-if="isLoading" />

    <div class="notification is-danger" v-if="gpsLoadError">{{ gpsLoadError }}</div>

    <!-- Top card -->
    <div class="card" v-if="gps">
      <!-- Top card header -->
      <header class="card-header">
        <div class="card-header-title">
          <div class="media">
            <div class="media-content">
              <p class="title is-4">IMEI {{ gps.imei }}</p>
            </div>
          </div>
        </div>

        <a class="card-header-icon" @click="toggleTopCardContentClicked">
          <span class="icon is-size-4">
            <i v-if="topCardContentVisible" class="icon-chevron-down"></i>
            <i v-if="!topCardContentVisible" class="icon-chevron-right"></i>
          </span>
        </a>
      </header>

      <!-- Top card content -->
      <div v-if="topCardContentVisible" class="card-content">
        <div class="content">
          <div class="table-container">
            <table class="table is-narrow user-details">
              <tbody>
                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">WeHunt GPS Owner</span>
                  </th>
                  <td>
                    <span v-if="owner"
                      >{{ owner.firstName }} {{ owner.lastName }} (<span
                        >{{ gps.gpsOwnerUserId }}_LLUser</span
                      >)</span
                    >
                    <span v-else>{{ gps.gpsOwnerUserId }}_LLUser</span>
                  </td>
                </tr>
                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">Vendor</span>
                  </th>
                  <td>
                    <span>{{ gps.vendor }}</span>
                  </td>
                </tr>
                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">Model</span>
                  </th>
                  <td>
                    <span>{{ gps.model }}</span>
                  </td>
                </tr>
                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">Phone</span>
                  </th>
                  <td>
                    <Spinner
                      class="is-pulled-left"
                      width="20"
                      height="20"
                      v-if="isLoadingDetails"
                    />
                    <span class="has-text-danger" v-if="detailsLoadError">{{
                      detailsLoadError
                    }}</span>
                    <span v-if="details">
                      {{ details.phoneNumber || '[unknown]' }}
                    </span>
                  </td>
                </tr>
                <tr>
                  <th class="is-narrow">
                    <span class="is-pulled-right">Warranty</span>
                  </th>
                  <td colspan="3">
                    <Spinner
                      class="is-pulled-left"
                      width="20"
                      height="20"
                      v-if="isLoadingWarrantyInfo"
                    />
                    <span class="has-text-danger" v-if="warrantyInfoLoadError">{{
                      warrantyInfoLoadError
                    }}</span>
                    <span v-if="warrantyInfo">
                      {{ warrantyInfo.message || 'none' }}
                    </span>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- Footer with buttons -->
      <footer v-if="topCardContentVisible" class="card-footer">
        <a v-if="owner" class="card-footer-item" @click="changeOwnerClicked">Change Owner</a>
        <a v-if="owner" class="card-footer-item" @click="changeIMEIClicked">Change IMEI</a>

        <Spinner v-if="!owner" class="is-size-3" />
      </footer>

      <!-- Modals -->
      <GPSChangeOwnerModal
        v-if="showChangeOwnerModal && owner"
        :gpsStorePath="weHuntGPSStorePath"
        :imei="gps.imei"
        :oldOwnerEmail="owner.email"
        @cancelled="closeChangeOwnerModal"
        @succeeded="closeChangeOwnerModal"
      />

      <GPSChangeIMEIModal
        v-if="showChangeIMEIModal && owner"
        :gpsStorePath="weHuntGPSStorePath"
        :imei="gps.imei"
        :email="owner.email"
        @cancelled="closeChangeIMEIModal"
        @succeeded="closeChangeIMEIModal"
      />

      <GPSDeleteDeviceModal
        v-if="deviceToDelete != null && gps"
        :gpsDeviceStorePath="gpsDeviceStorePath"
        :gps="deviceToDelete"
        @cancelled="closeDeleteDeviceModal"
        @succeeded="deviceDeleted"
      />
    </div>

    <!-- Bottom tabs -->
    <div class="tabs is-toggle is-fullwidth bottom-tabs" v-if="gps">
      <ul>
        <li
          v-for="tab in bottomTabs"
          :key="tab.index"
          :class="{ 'is-active': tab.index === selectedBottomTabIndex }"
          @click="selectBottomTab(tab)"
        >
          <a>{{ tab.name }}</a>
        </li>
      </ul>
    </div>

    <!-- Bottom tab content -->
    <div class="tabs-content" v-if="gps">
      <component
        :is="selectedBottomTabComponent"
        :gpsStorePath="weHuntGPSStorePath"
        :weHuntGPSStorePath="weHuntGPSStorePath"
        :usersCacheStorePath="usersCacheStorePath"
        @delete-gps-device="deleteGPSDevice($event)"
        @gps-device-selected="gpsDeviceSelected($event)"
      ></component>
    </div>
  </div>
</template>

<script>
import Spinner from '@/components/utils/Spinner'
import GPSCoverageMap from './GPSCoverageMap'
import WeHuntGPSDevicesTab from './WeHuntGPSDevicesTab'
import GPSChangeOwnerModal from './GPSChangeOwnerModal'
import GPSChangeIMEIModal from './GPSChangeIMEIModal'
import GPSDeleteDeviceModal from './GPSDeleteDeviceModal'

import {
  LOAD_GPS_WARRANTY_INFO,
  LOAD_GPS_DETAILS,
  IS_LOADING_GPS,
  GET_GPS,
  GET_GPS_LOAD_ERROR,
  IS_LOADING_WARRANTY_INFO,
  GET_WARRANTY_INFO,
  GET_WARRANTY_INFO_LOAD_ERROR,
  IS_LOADING_DETAILS,
  GET_DETAILS,
  GET_DETAILS_LOAD_ERROR,
  RELOAD_GPS_DEVICES
} from './weHuntGPSStore.js'

import { LOAD_USERS_WITH_IDS, GET_USER_WITH_ID } from '@/modules/users/usersCacheStore.js'

export default {
  name: 'WeHuntGPS',

  components: {
    Spinner,
    GPSCoverageMap,
    WeHuntGPSDevicesTab,
    GPSChangeOwnerModal,
    GPSChangeIMEIModal,
    GPSDeleteDeviceModal
  },

  props: {
    weHuntGPSStorePath: {
      type: String,
      required: true
    },

    gpsDeviceStorePath: {
      type: String,
      required: true
    },

    usersCacheStorePath: {
      type: String,
      required: true
    }
  },

  data() {
    return {
      showBackLink: false,
      showChangeOwnerModal: false,
      showChangeIMEIModal: false,
      topCardContentVisible: true,
      deviceToDelete: null,

      bottomTabs: [
        {
          index: 0,
          name: 'Map',
          component: GPSCoverageMap
        },
        {
          index: 1,
          name: 'GPS Devices',
          component: WeHuntGPSDevicesTab
        }
      ],
      selectedBottomTabIndex: null
    }
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.showBackLink = from.name === 'gpsDetails'
    })
  },

  mounted() {
    this.loadOwnerUser()
    this.loadWarrantyInfo()
    this.loadDetails()
  },

  methods: {
    backLinkClicked() {
      this.$router.go(-1)
    },

    selectBottomTab(tab) {
      this.selectedBottomTabIndex = tab.index
    },

    loadOwnerUser() {
      if (!this.gps) {
        return
      }

      const loadUserWithIdsAction = `${this.usersCacheStorePath}/${LOAD_USERS_WITH_IDS}`
      const userIds = [this.gps.gpsOwnerUserId]
      this.$store.dispatch(loadUserWithIdsAction, { userIds })
    },

    loadWarrantyInfo() {
      if (!this.gps) {
        return
      }

      const loadGPSWarrantyInfoAction = `${this.weHuntGPSStorePath}/${LOAD_GPS_WARRANTY_INFO}`
      const imei = this.gps.imei
      this.$store.dispatch(loadGPSWarrantyInfoAction, { imei })
    },

    loadDetails() {
      if (!this.gps) {
        return
      }

      const loadDetailsAction = `${this.weHuntGPSStorePath}/${LOAD_GPS_DETAILS}`
      const imei = this.gps.imei
      this.$store.dispatch(loadDetailsAction, { imei })
    },

    changeOwnerClicked() {
      if (!this.owner) {
        return
      }

      this.showChangeOwnerModal = true
    },

    closeChangeOwnerModal() {
      this.showChangeOwnerModal = false
    },

    changeIMEIClicked() {
      this.showChangeIMEIModal = true
    },

    closeChangeIMEIModal() {
      this.showChangeIMEIModal = false
    },

    toggleTopCardContentClicked() {
      this.topCardContentVisible = !this.topCardContentVisible
    },

    deleteGPSDevice(gpsDevice) {
      this.deviceToDelete = gpsDevice
    },

    gpsDeviceSelected(gpsDevice) {
      const deviceId = gpsDevice.deviceId
      this.$router.push({
        name: 'gpsDetails',
        params: { deviceId, showBackLink: true },
        query: this.$route.query
      })
    },

    deviceDeleted() {
      this.deviceToDelete = null

      const reloadGPSDevicesAction = `${this.weHuntGPSStorePath}/${RELOAD_GPS_DEVICES}`
      this.$store.dispatch(reloadGPSDevicesAction)
    },

    closeDeleteDeviceModal() {
      this.deviceToDelete = null
    }
  },

  computed: {
    backLinkText() {
      return '<< Back'
    },

    isLoading() {
      const isLoadingGPSPath = `${this.weHuntGPSStorePath}/${IS_LOADING_GPS}`
      return this.$store.getters[isLoadingGPSPath]
    },

    gps() {
      const getGPSPath = `${this.weHuntGPSStorePath}/${GET_GPS}`
      return this.$store.getters[getGPSPath]
    },

    owner() {
      if (!this.gps) {
        return null
      }

      if (!this.gps.gpsOwnerUserId) {
        return null
      }

      const getUserWithIdPath = `${this.usersCacheStorePath}/${GET_USER_WITH_ID}`
      return this.$store.getters[getUserWithIdPath](this.gps.gpsOwnerUserId)
    },

    gpsLoadError() {
      const getGPSLoadErrorPath = `${this.weHuntGPSStorePath}/${GET_GPS_LOAD_ERROR}`
      return this.$store.getters[getGPSLoadErrorPath]
    },

    isLoadingWarrantyInfo() {
      const isLoadingWarrantyInfoPath = `${this.weHuntGPSStorePath}/${IS_LOADING_WARRANTY_INFO}`
      return this.$store.getters[isLoadingWarrantyInfoPath]
    },

    warrantyInfo() {
      const getWarrantyInfoPath = `${this.weHuntGPSStorePath}/${GET_WARRANTY_INFO}`
      return this.$store.getters[getWarrantyInfoPath]
    },

    warrantyInfoLoadError() {
      const getWarrantyInfoLoadErrorPath = `${this.weHuntGPSStorePath}/${GET_WARRANTY_INFO_LOAD_ERROR}`
      return this.$store.getters[getWarrantyInfoLoadErrorPath]
    },

    isLoadingDetails() {
      const isLoadingDetailsPath = `${this.weHuntGPSStorePath}/${IS_LOADING_DETAILS}`
      return this.$store.getters[isLoadingDetailsPath]
    },

    details() {
      const getDetailsPath = `${this.weHuntGPSStorePath}/${GET_DETAILS}`
      return this.$store.getters[getDetailsPath]
    },

    detailsLoadError() {
      const getDetailsLoadErrorPath = `${this.weHuntGPSStorePath}/${GET_DETAILS_LOAD_ERROR}`
      const loadError = this.$store.getters[getDetailsLoadErrorPath]
      if (!loadError) {
        return null
      }

      if (loadError.message && loadError.message.includes('status code 404')) {
        return '[unknown]'
      }

      return loadError
    },

    selectedBottomTabComponent() {
      if (this.selectedBottomTabIndex === null) {
        return null
      }

      return this.bottomTabs[this.selectedBottomTabIndex].component
    }
  },

  watch: {
    gps() {
      this.showChangeOwnerModal = false
      this.showChangeIMEIModal = false
      this.deviceToDelete = null
      this.loadOwnerUser()
      this.loadWarrantyInfo()
      this.loadDetails()
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/style.scss';

.header {
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;

  a {
    margin-right: 0.75rem;
  }
}

.warranty-tag {
  margin-left: 0.5rem;
}

.gps-main {
  padding: 10px;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;

  .map-container {
    height: 100%;
    width: 100%;
    padding-top: 10px;
  }
}

.card-content {
  .table {
    th {
      padding-right: 0 !important;
      border-bottom: 0;

      span {
        font-weight: normal;
      }
    }

    td {
      border-bottom: 0;

      span {
        font-weight: bold;
        white-space: nowrap;
      }
    }
  }
}

.card-footer {
  min-height: 48px;

  .loader {
    margin: auto;
  }
}

.bottom-tabs.tabs {
  margin-top: $column-gap * 2;

  a {
    padding: 4px 16px;
  }
}

.tabs-content {
  flex-grow: 1;
  overflow: auto;
}
</style>
